import React from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from 'recharts';
import { COLOR_OJO, aTiempo, fechaCorta, tickFecha } from '../../lib/oftalmo';

/**
 * Una medida de los dos ojos en el tiempo (PIO, grosor central, MD, Kmax,
 * área de una lesión…), con lo que ayuda a leerla:
 *   · `objetivo` = { od, oi }: línea discontinua de cada ojo (PIO objetivo);
 *   · `marcas` = [{ fecha, texto, ojo }]: líneas verticales (una inyección,
 *     un cambio de tratamiento, la cirugía).
 * `puntos` = [{ fecha, od, oi }] (cualquiera de los dos puede faltar).
 */
export default function GraficaOjos({
  puntos, unidad = '', objetivo = null, marcas = [], alto = 260, dominio = ['auto', 'auto'],
  decimales = 0, testId, invertir = false,
}) {
  const datos = (puntos || [])
    .filter((p) => p.fecha && (p.od !== null && p.od !== undefined || p.oi !== null && p.oi !== undefined))
    .map((p) => ({ ...p, t: aTiempo(p.fecha) }))
    .sort((a, b) => a.t - b.t);
  if (!datos.length) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Sin datos todavía.</div>;
  }
  const hayOD = datos.some((p) => p.od !== null && p.od !== undefined);
  const hayOI = datos.some((p) => p.oi !== null && p.oi !== undefined);
  const fmt = (v) => (typeof v === 'number' ? v.toFixed(decimales).replace('.', ',') : v);
  const ts = datos.map((d) => d.t).concat((marcas || []).map((m) => aTiempo(m.fecha)));
  const min = Math.min(...ts);
  const max = Math.max(...ts);
  const margen = Math.max((max - min) * 0.04, 86400000 * 3);
  // Las marcas del eje, a mano y sin repetir: con una serie de pocos días la
  // escala de tiempo de recharts repetía la misma fecha (y React avisaba).
  const desde = min - margen;
  const hasta = max + margen;
  const marcasEje = [...new Set(Array.from({ length: 6 }, (_, i) => {
    const t = desde + ((hasta - desde) * i) / 5;
    return Math.round(t / 86400000) * 86400000;
  }))];
  return (
    <div data-testid={testId} style={{ width: '100%', height: alto }}>
      <ResponsiveContainer>
        <LineChart data={datos} margin={{ top: 12, right: 18, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,140,135,0.25)" />
          <XAxis dataKey="t" type="number" scale="time" domain={[desde, hasta]} ticks={marcasEje}
            tickFormatter={(t) => tickFecha(t, max - min < 86400000 * 120)} tick={{ fontSize: 11 }} />
          <YAxis domain={dominio} tick={{ fontSize: 11 }} width={46} reversed={invertir}
            tickFormatter={fmt} />
          <Tooltip
            labelFormatter={(t) => fechaCorta(new Date(t).toISOString())}
            formatter={(v, n) => [`${fmt(v)} ${unidad}`.trim(), n]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {(marcas || []).map((m, i) => (
            <ReferenceLine key={`m${i}`} x={aTiempo(m.fecha)} stroke={m.ojo ? COLOR_OJO[m.ojo] : '#64748B'}
              strokeDasharray="2 4" strokeOpacity={0.55}
              label={m.texto ? { value: m.texto, position: 'insideTop', fontSize: 10, fill: '#475569' } : undefined} />
          ))}
          {objetivo?.od !== null && objetivo?.od !== undefined && hayOD && (
            <ReferenceLine y={objetivo.od} stroke={COLOR_OJO.OD} strokeDasharray="6 4"
              label={{ value: `objetivo OD ${objetivo.od}`, position: 'right', fontSize: 10, fill: COLOR_OJO.OD }} />
          )}
          {objetivo?.oi !== null && objetivo?.oi !== undefined && hayOI && (
            <ReferenceLine y={objetivo.oi} stroke={COLOR_OJO.OI} strokeDasharray="6 4"
              label={{ value: `objetivo OI ${objetivo.oi}`, position: 'right', fontSize: 10, fill: COLOR_OJO.OI }} />
          )}
          {hayOD && <Line type="linear" dataKey="od" name="OD" stroke={COLOR_OJO.OD} strokeWidth={2.2}
            dot={{ r: 3.5 }} activeDot={{ r: 5 }} connectNulls isAnimationActive={false} />}
          {hayOI && <Line type="linear" dataKey="oi" name="OI" stroke={COLOR_OJO.OI} strokeWidth={2.2}
            dot={{ r: 3.5 }} activeDot={{ r: 5 }} connectNulls isAnimationActive={false} />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
